import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { defined } from '@platform/schema';
import { stepName } from '@platform/schema/onboarding-flow';
import { useCallback } from 'react';

import { ChatPanel } from '../authoring/ChatPanel';

import { replaceStep, reword } from './config';

import type { OnboardingReview } from '../authoring/ChatPanel';
import type { Change } from '@platform/schema/changes';
import type { OnboardingProposal } from '@platform/schema/chat';
import type {
  FlowStep,
  OnboardingConfig,
  OnboardingCopyCatalog,
} from '@platform/schema/onboarding';

/**
 * The planner beside the onboarding editor — ONB-21. An applied proposal lands in the
 * page's unsaved config, so it is reviewed, saved and published like a hand edit.
 */

const FIELD: Record<string, string> = {
  name: 'Name',
  title: 'Title',
  body: 'Body',
  button: 'Button',
  skipLabel: 'Skip button',
};

/**
 * What a proposal would change against `config`, and the config with it applied.
 * Pure, so Apply all can fold several over one draft — ADMIN-27.
 */
function proposalAgainst(
  edit: OnboardingProposal,
  config: OnboardingConfig,
  catalog: OnboardingCopyCatalog,
): { changes: Change[]; next: OnboardingConfig } | undefined {
  if (edit.kind === 'onboardingCopy') {
    const english = catalog.entries.find(
      (one) => one.key === edit.key,
    )?.english;
    if (english === undefined) return undefined;
    const was =
      config.copy.find((row) => row.key === edit.key)?.text ?? english;
    return {
      changes:
        was === edit.text
          ? []
          : [{ field: edit.key, before: was, after: edit.text }],
      next: reword(config, edit.key, edit.text, english),
    };
  }

  const step = config.flow.find((one) => one.id === edit.stepId);
  if (step === undefined || step.kind === 'BUILT_IN') return undefined;
  const { skipLabel, ...words } = defined(edit.body);
  if (step.kind === 'INFO' && skipLabel !== undefined) return undefined;

  const proposed = {
    ...words,
    ...(skipLabel === undefined ? {} : { skipLabel }),
  };
  const current: Record<string, string | undefined> = {
    name: step.name,
    title: step.title,
    body: step.body,
    button: step.button,
    skipLabel: step.kind === 'QUESTION' ? step.skipLabel : undefined,
  };
  const changes: Change[] = Object.entries(proposed).flatMap(
    ([field, after]) =>
      current[field] === after
        ? []
        : [
            {
              field: FIELD[field] ?? field,
              before: current[field] ?? '',
              after,
            },
          ],
  );
  const next: FlowStep = { ...step, ...proposed };

  return {
    changes,
    next: { ...config, flow: replaceStep(config.flow, next) },
  };
}

/** What a proposal would change against `config`, and how to apply it there. */
export function reviewOnboarding(
  edit: OnboardingProposal,
  config: OnboardingConfig,
  catalog: OnboardingCopyCatalog,
  onConfig: (next: OnboardingConfig) => void,
): OnboardingReview {
  const against = proposalAgainst(edit, config, catalog);
  if (against === undefined)
    return { changes: undefined, apply: () => undefined };

  return {
    changes: against.changes,
    apply: () => {
      onConfig(against.next);
    },
  };
}

/** Every edit applied in turn, each against the draft the one before it left. */
export function applyOnboarding(
  edits: readonly OnboardingProposal[],
  config: OnboardingConfig,
  catalog: OnboardingCopyCatalog,
): OnboardingConfig {
  return edits.reduce(
    (draft, edit) => proposalAgainst(edit, draft, catalog)?.next ?? draft,
    config,
  );
}

export function OnboardingChat({
  open,
  step,
  config,
  catalog,
  disabled,
  onConfig,
  onClose,
}: {
  open: boolean;
  step: FlowStep;
  config: OnboardingConfig;
  catalog: OnboardingCopyCatalog;
  disabled: boolean;
  onConfig: (next: OnboardingConfig) => void;
  onClose: () => void;
}) {
  const review = useCallback(
    (edit: OnboardingProposal) =>
      reviewOnboarding(edit, config, catalog, onConfig),
    [config, catalog, onConfig],
  );
  const applyAll = useCallback(
    (edits: readonly OnboardingProposal[]) => {
      onConfig(applyOnboarding(edits, config, catalog));
    },
    [config, catalog, onConfig],
  );

  // Over the preview rather than beside it: four columns do not fit at 1440px. Hidden,
  // not unmounted, so closing it keeps every screen's conversation.
  return (
    <Paper
      elevation={4}
      aria-label="Planner"
      sx={{
        display: open ? 'flex' : 'none',
        flexDirection: 'column',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: '24rem',
        p: 2,
        zIndex: 1,
      }}
    >
      <Button size="small" sx={{ alignSelf: 'flex-end' }} onClick={onClose}>
        Close the planner
      </Button>
      <ChatPanel
        subject={{
          kind: 'onboarding',
          stepId: step.id,
          name: stepName(step),
          review,
          applyAll,
        }}
        disabled={disabled}
      />
    </Paper>
  );
}
